// Notes — private member notes with attachments and meeting/content links.

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  BookOpen,
  Calendar,
  Camera,
  Download,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  NotebookPen,
  Paperclip,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { contentAPI, notesAPI } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import WebcamCaptureModal from '../components/WebcamCaptureModal';
import { isCameraCancel } from '../native/camera';
import type { LinkableMeetings, Note, NoteAttachment, Teaching } from '../types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMeetingOption(starts_at: string | null, label: string): string {
  if (!starts_at) return label;
  return `${label} · ${formatDate(starts_at)}`;
}

/** Loads an attachment through the auth-gated API and returns a usable URL. */
function useAttachmentUrl(attachment: NoteAttachment) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = '';
    let cancelled = false;
    setSrc('');
    setFailed(false);
    notesAPI.attachmentObjectUrl(attachment.url).then((url) => {
      if (cancelled) {
        if (url.startsWith('blob:')) window.URL.revokeObjectURL(url);
        return;
      }
      if (url.startsWith('blob:')) revoked = url;
      setSrc(url);
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (revoked) window.URL.revokeObjectURL(revoked);
    };
  }, [attachment.url]);

  return { src, failed };
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Image attachment thumbnail fetched through the auth-gated API. */
function AttachmentThumb({ attachment, onOpen }: { attachment: NoteAttachment; onOpen: () => void }) {
  const { src } = useAttachmentUrl(attachment);

  if (!src) return <span className="block h-16 w-16 rounded-lg border bg-off-white" />;
  return (
    <button type="button" onClick={onOpen} aria-label={`View ${attachment.filename}`}>
      <img
        src={src}
        alt={attachment.filename}
        className="h-16 w-16 rounded-lg border object-cover transition hover:opacity-80"
      />
    </button>
  );
}

/** In-app viewer: images full-size, PDFs embedded, everything else downloads. */
function AttachmentViewerModal({
  attachment,
  onClose,
}: {
  attachment: NoteAttachment;
  onClose: () => void;
}) {
  const { src, failed } = useAttachmentUrl(attachment);
  const isPdf =
    attachment.content_type === 'application/pdf' ||
    attachment.filename.toLowerCase().endsWith('.pdf');

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-xl w-full overflow-hidden flex flex-col ${
          isPdf ? 'max-w-4xl h-[85vh]' : 'max-w-2xl max-h-[85vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-navy text-sm">{attachment.filename}</p>
            <p className="text-xs text-gray-400">{formatSize(attachment.size)}</p>
          </div>
          {src && (
            <a
              href={src}
              download={attachment.filename}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-navy border border-gray-200 rounded-lg hover:bg-off-white transition"
            >
              <Download size={14} />
              Download
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close viewer"
            className="p-1 text-gray-400 hover:text-navy transition"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto bg-off-white flex items-center justify-center">
          {failed ? (
            <p className="p-8 text-sm text-gray-600">Could not load this attachment.</p>
          ) : !src ? (
            <Loader2 size={26} className="animate-spin text-gray-400 my-16" />
          ) : attachment.is_image ? (
            <img
              src={src}
              alt={attachment.filename}
              className="max-h-full max-w-full object-contain p-2"
            />
          ) : isPdf ? (
            <iframe src={src} title={attachment.filename} className="h-full w-full border-0" />
          ) : (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <FileText size={36} className="text-gray-300" />
              <p className="text-sm text-gray-600">
                No preview available for this file type.
              </p>
              <a
                href={src}
                download={attachment.filename}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-light transition"
              >
                <Download size={15} />
                Download {attachment.filename}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Notes() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');

  // Editor state. `editing` is the persisted note being edited (null = new).
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [teachingId, setTeachingId] = useState<number | null>(null);
  const [occurrenceId, setOccurrenceId] = useState('');
  const [meetingRequestId, setMeetingRequestId] = useState<number | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [viewing, setViewing] = useState<NoteAttachment | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Note | null>(null);

  // Link picker data, loaded once when the editor first opens.
  const [teachings, setTeachings] = useState<Teaching[]>([]);
  const [meetings, setMeetings] = useState<LinkableMeetings | null>(null);
  const [pickersLoaded, setPickersLoaded] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const load = async (search?: string) => {
    try {
      setNotes(await notesAPI.list(search));
    } catch {
      toast.error('Could not load your notes.');
    } finally {
      setIsLoading(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    load();
    if (searchParams.get('new') === '1') {
      openEditor(null);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => load(query.trim() || undefined), 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const loadPickers = async () => {
    if (pickersLoaded) return;
    setPickersLoaded(true);
    try {
      const [teachingList, meetingList] = await Promise.all([
        contentAPI.getTeachings(),
        notesAPI.linkableMeetings(),
      ]);
      setTeachings(teachingList);
      setMeetings(meetingList);
    } catch {
      // Pickers degrade to empty dropdowns; the note itself still works.
    }
  };

  const openEditor = (note: Note | null) => {
    setEditing(note);
    setTitle(note?.title || '');
    setBody(note?.body || '');
    setTeachingId(note?.teaching ?? null);
    setOccurrenceId(note?.event ? `event-${note.event}` : '');
    setMeetingRequestId(note?.meeting_request ?? null);
    setPendingFiles([]);
    setEditorOpen(true);
    loadPickers();
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setPendingFiles([]);
  };

  const applySaved = (saved: Note) => {
    setEditing(saved);
    setNotes((current) => {
      const rest = current.filter((n) => n.id !== saved.id);
      return [saved, ...rest];
    });
  };

  const save = async () => {
    if (!title.trim() && !body.trim() && pendingFiles.length === 0 && !editing) {
      toast.error('Write something (or add a file) first.');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        title: title.trim(),
        body,
        teaching: teachingId,
        meeting_request: meetingRequestId,
        occurrence: occurrenceId,
      };
      let saved = editing
        ? await notesAPI.update(editing.id, payload)
        : await notesAPI.create(payload);
      for (const file of pendingFiles) {
        saved = await notesAPI.addAttachment(saved.id, file);
      }
      setPendingFiles([]);
      applySaved(saved);
      toast.success('Note saved.');
      closeEditor();
    } catch {
      toast.error('Could not save the note.');
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await notesAPI.remove(confirmDelete.id);
      setNotes((current) => current.filter((n) => n.id !== confirmDelete.id));
      if (editing?.id === confirmDelete.id) closeEditor();
      toast.success('Note deleted.');
    } catch {
      toast.error('Could not delete the note.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const attachFiles = async (files: File[]) => {
    if (files.length === 0) return;
    if (!editing) {
      setPendingFiles((current) => [...current, ...files]);
      return;
    }
    setIsUploading(true);
    try {
      let updated = editing;
      for (const file of files) {
        updated = await notesAPI.addAttachment(editing.id, file);
      }
      applySaved(updated);
    } catch {
      toast.error('Could not attach that file. Use an image, PDF, or Office document (max 15MB).');
    } finally {
      setIsUploading(false);
    }
  };

  const onFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    attachFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const takePhoto = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { Camera: NativeCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await NativeCamera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
          allowEditing: false,
        });
        if (photo.webPath) {
          const blob = await (await fetch(photo.webPath)).blob();
          const file = new File([blob], `photo-${Date.now()}.jpg`, {
            type: blob.type || 'image/jpeg',
          });
          attachFiles([file]);
        }
      } catch (err) {
        if (!isCameraCancel(err)) toast.error('Could not access the camera.');
      }
    } else if (typeof navigator.mediaDevices?.getUserMedia === 'function') {
      setShowWebcam(true);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const removeAttachment = async (attachment: NoteAttachment) => {
    if (!editing) return;
    try {
      applySaved(await notesAPI.removeAttachment(editing.id, attachment.id));
    } catch {
      toast.error('Could not remove that attachment.');
    }
  };

  const openAttachment = (attachment: NoteAttachment) => setViewing(attachment);

  const linkChips = (note: Note) => {
    const chips: { icon: typeof BookOpen; label: string }[] = [];
    if (note.teaching_title) chips.push({ icon: BookOpen, label: note.teaching_title });
    if (note.event_label) chips.push({ icon: Calendar, label: note.event_label });
    if (note.meeting_label) chips.push({ icon: Users, label: note.meeting_label });
    return chips;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <NotebookPen size={24} /> Notes
          </h1>
          <p className="text-gray-600">
            Private notes — capture takeaways from meetings, teachings, and 1:1s.
          </p>
        </div>
        <button
          onClick={() => openEditor(null)}
          className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-white hover:bg-navy-dark transition"
        >
          <Plus size={18} /> New note
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search your notes…"
          className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-4 outline-none focus:ring-2 focus:ring-navy"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-navy" />
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm">
          <NotebookPen size={36} className="mx-auto text-gray-300" />
          <p className="mt-3 font-semibold text-navy">
            {query ? 'No notes match that search.' : 'No notes yet'}
          </p>
          {!query && (
            <p className="mt-1 text-sm text-gray-500">
              Jot down thoughts, snap a photo of a whiteboard, or keep takeaways
              from a forum meeting.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <button
              key={note.id}
              onClick={() => openEditor(note)}
              className="w-full rounded-2xl bg-white p-5 text-left shadow-sm hover:shadow transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-navy">{note.title || '(untitled)'}</p>
                  {note.body && (
                    <p className="mt-1 line-clamp-2 text-sm text-gray-600 whitespace-pre-line">
                      {note.body}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-gray-400">{formatDate(note.updated_at)}</span>
              </div>
              {(linkChips(note).length > 0 || note.attachments.length > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {linkChips(note).map(({ icon: Icon, label }) => (
                    <span
                      key={label}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-off-white px-2.5 py-1 text-xs text-navy"
                    >
                      <Icon size={12} className="shrink-0" />
                      <span className="truncate">{label}</span>
                    </span>
                  ))}
                  {note.attachments.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-off-white px-2.5 py-1 text-xs text-navy">
                      <Paperclip size={12} />
                      {note.attachments.length}
                    </span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {editorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-navy">
                {editing ? 'Edit note' : 'New note'}
              </h2>
              <div className="flex items-center gap-1">
                {editing && (
                  <button
                    onClick={() => setConfirmDelete(editing)}
                    className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                    aria-label="Delete note"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                <button
                  onClick={closeEditor}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Title"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 font-semibold text-navy outline-none focus:border-navy"
              />
              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="Write your note…"
                rows={8}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-navy"
              />

              <section className="space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-navy">
                  <Paperclip size={15} /> Attachments
                </p>
                {(editing?.attachments?.length || pendingFiles.length > 0) ? (
                  <div className="space-y-2">
                    {(editing?.attachments || []).filter((a) => a.is_image).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(editing?.attachments || [])
                          .filter((a) => a.is_image)
                          .map((attachment) => (
                            <span key={attachment.id} className="relative">
                              <AttachmentThumb
                                attachment={attachment}
                                onOpen={() => openAttachment(attachment)}
                              />
                              <button
                                onClick={() => removeAttachment(attachment)}
                                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-500 shadow hover:text-red-600"
                                aria-label={`Remove ${attachment.filename}`}
                              >
                                <X size={13} />
                              </button>
                            </span>
                          ))}
                      </div>
                    )}
                    <ul className="space-y-1">
                      {(editing?.attachments || [])
                        .filter((a) => !a.is_image)
                        .map((attachment) => (
                          <li key={attachment.id} className="flex items-center gap-2 text-sm">
                            <FileText size={14} className="shrink-0 text-gray-400" />
                            <button
                              onClick={() => openAttachment(attachment)}
                              className="truncate text-navy underline"
                            >
                              {attachment.filename}
                            </button>
                            <button
                              onClick={() => removeAttachment(attachment)}
                              className="rounded p-1 text-gray-400 hover:text-red-600"
                              aria-label={`Remove ${attachment.filename}`}
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      {pendingFiles.map((file, index) => (
                        <li key={`${file.name}-${index}`} className="flex items-center gap-2 text-sm text-gray-600">
                          {file.type.startsWith('image/')
                            ? <ImageIcon size={14} className="shrink-0 text-gray-400" />
                            : <FileText size={14} className="shrink-0 text-gray-400" />}
                          <span className="truncate">{file.name}</span>
                          <span className="text-xs text-gold">added on save</span>
                          <button
                            onClick={() =>
                              setPendingFiles((current) => current.filter((_, i) => i !== index))
                            }
                            className="rounded p-1 text-gray-400 hover:text-red-600"
                            aria-label={`Remove ${file.name}`}
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">None yet</p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-navy/20 px-3 py-1.5 text-sm font-medium text-navy hover:bg-navy/5 disabled:opacity-50"
                  >
                    <Paperclip size={15} /> Add file
                  </button>
                  <button
                    onClick={takePhoto}
                    disabled={isUploading}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-navy/20 px-3 py-1.5 text-sm font-medium text-navy hover:bg-navy/5 disabled:opacity-50"
                  >
                    <Camera size={15} /> Take photo
                  </button>
                  {isUploading && <Loader2 size={18} className="animate-spin self-center text-navy" />}
                </div>
              </section>

              <section className="space-y-3 rounded-xl border border-gray-200 p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-navy">
                  <Link2 size={15} /> Link this note
                </p>
                <label className="block text-sm">
                  <span className="text-gray-600">Library content</span>
                  <select
                    value={teachingId ?? ''}
                    onChange={(event) => setTeachingId(Number(event.target.value) || null)}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-navy"
                  >
                    <option value="">None</option>
                    {teachings.map((teaching) => (
                      <option key={teaching.id} value={teaching.id}>{teaching.title}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Forum meeting</span>
                  <select
                    value={occurrenceId}
                    onChange={(event) => setOccurrenceId(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-navy"
                  >
                    <option value="">None</option>
                    {editing?.event && !meetings?.forum_meetings?.some(
                      (m) => m.occurrence_id === `event-${editing.event}`
                    ) && (
                      <option value={`event-${editing.event}`}>{editing.event_label}</option>
                    )}
                    {(meetings?.forum_meetings || [])
                      .filter((meeting) => meeting.kind !== 'member_meeting')
                      .map((meeting) => (
                        <option key={meeting.occurrence_id} value={meeting.occurrence_id}>
                          {formatMeetingOption(meeting.starts_at, meeting.title || meeting.forum_name)}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">1:1 meeting</span>
                  <select
                    value={meetingRequestId ?? ''}
                    onChange={(event) => setMeetingRequestId(Number(event.target.value) || null)}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-navy"
                  >
                    <option value="">None</option>
                    {(meetings?.one_on_ones || []).map((meeting) => (
                      <option key={meeting.id} value={meeting.id}>
                        {formatMeetingOption(meeting.starts_at, `${meeting.title} — with ${meeting.with_name}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </section>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={closeEditor}
                className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSaving || isUploading}
                className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-2 text-white disabled:opacity-50"
              >
                {isSaving && <Loader2 size={16} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.txt,.csv,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        className="sr-only"
        onChange={onFilesPicked}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={onFilesPicked}
      />

      {viewing && (
        <AttachmentViewerModal attachment={viewing} onClose={() => setViewing(null)} />
      )}

      {showWebcam && (
        <WebcamCaptureModal
          facingMode="environment"
          onCapture={(blob) => {
            setShowWebcam(false);
            attachFiles([
              new File([blob], `photo-${Date.now()}.png`, { type: 'image/png' }),
            ]);
          }}
          onCancel={() => setShowWebcam(false)}
          onPickFileInstead={() => {
            setShowWebcam(false);
            cameraInputRef.current?.click();
          }}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(confirmDelete)}
        title="Delete note"
        message={`Delete “${confirmDelete?.title || 'this note'}”? Its attachments are removed too.`}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}
